import { EmailMessageDraft, EmailReference, LexicalText } from "@/schema"
import { create } from "zustand"

export interface PendingAttachment {
  file: File
  id: string
}

export interface TemplateAttachment {
  signed_id: string
  file_name: string
  url?: string
}

const RECIPIENT_FIELDS = ["to_recipients", "cc_recipients", "bcc_recipients"] as const

interface EmailComposerState {
  drafts: Record<string, EmailMessageDraft>
  attachments: Record<string, PendingAttachment[]>
  templateAttachments: Record<string, TemplateAttachment[]>
  signatureAppendedDrafts: Set<string>
  createDraft: (starterMessage?: EmailMessageDraft) => string
  setSubject: (draftId: string, subject: string) => void
  setContent: (draftId: string, content: LexicalText) => void
  addRecipient: (
    draftId: string,
    type: "to_recipients" | "cc_recipients" | "bcc_recipients",
    recipient: EmailReference
  ) => void
  removeRecipient: (draftId: string, type: "to_recipients" | "cc_recipients" | "bcc_recipients", email: string) => void
  setFrom: (draftId: string, from: EmailReference, integrationId?: string) => void
  toggleCC: (draftId: string) => void
  toggleBCC: (draftId: string) => void
  removeDraft: (draftId: string) => void
  addAttachment: (draftId: string, file: File) => void
  removeAttachment: (draftId: string, attachmentId: string) => void
  setTemplateAttachments: (draftId: string, attachments: TemplateAttachment[]) => void
  removeTemplateAttachment: (draftId: string, signedId: string) => void
  markSignatureAppended: (draftId: string) => void
  hasSignatureAppended: (draftId: string) => boolean
}

const DEFAULT_DRAFT: EmailMessageDraft = {
  id: "",
  subject: "",
  status: "pending",
  created_at: new Date(),
  updated_at: new Date(),
  to_recipients: [],
  cc_recipients: [],
  bcc_recipients: [],
  from: { email: "" },
  showCC: false,
  showBCC: false,
  household_id: undefined,
}

export const useEmailComposerStore = create<EmailComposerState>()((set, get) => ({
  drafts: {},
  attachments: {},
  templateAttachments: {},
  signatureAppendedDrafts: new Set<string>(),

  createDraft: (starterDraft?: EmailMessageDraft) => {
    const secondPrecisionId = Math.round(Date.now() / 2000)
    const draftId = secondPrecisionId.toString()
    const startingDraft = { ...DEFAULT_DRAFT, ...(starterDraft || {}) }
    set((state) => {
      return {
        drafts: { ...state.drafts, [draftId]: { ...startingDraft } },
        attachments: { ...state.attachments, [draftId]: [] },
        templateAttachments: { ...state.templateAttachments, [draftId]: [] },
      }
    })
    return draftId
  },

  setSubject: (draftId: string, subject: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, subject } },
      }
    }),

  setContent: (draftId: string, content: LexicalText) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, content } },
      }
    }),

  addRecipient: (
    draftId: string,
    type: "to_recipients" | "cc_recipients" | "bcc_recipients",
    recipient: EmailReference
  ) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      const recipientEmail = recipient.email.toLowerCase()
      const recipientAlreadyAdded = RECIPIENT_FIELDS.some((recipientField) =>
        draft[recipientField]?.some((existingRecipient) => existingRecipient.email.toLowerCase() === recipientEmail)
      )
      if (recipientAlreadyAdded) {
        return state
      }
      const updatedRecipients = [...(draft[type] || []), recipient]
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, [type]: updatedRecipients } },
      }
    }),

  removeRecipient: (draftId: string, type: "to_recipients" | "cc_recipients" | "bcc_recipients", email: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      const updatedRecipients = draft[type]?.filter((r) => r.email !== email) || []
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, [type]: updatedRecipients } },
      }
    }),

  setFrom: (draftId: string, from: EmailReference, integrationId?: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: {
          ...state.drafts,
          [draftId]: { ...draft, from, email_integration_id: integrationId ?? draft.email_integration_id },
        },
      }
    }),

  toggleCC: (draftId: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, showCC: !draft.showCC } },
      }
    }),

  toggleBCC: (draftId: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, showBCC: !draft.showBCC } },
      }
    }),

  removeDraft: (draftId: string) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      const newDrafts = { ...state.drafts }
      const newAttachments = { ...state.attachments }
      const newTemplateAttachments = { ...state.templateAttachments }
      const newSignatureAppendedDrafts = new Set(state.signatureAppendedDrafts)
      delete newDrafts[draftId]
      delete newAttachments[draftId]
      delete newTemplateAttachments[draftId]
      newSignatureAppendedDrafts.delete(draftId)
      return {
        drafts: newDrafts,
        attachments: newAttachments,
        templateAttachments: newTemplateAttachments,
        signatureAppendedDrafts: newSignatureAppendedDrafts,
      }
    }),

  addAttachment: (draftId: string, file: File) =>
    set((state) => {
      const attachments = state.attachments[draftId] || []
      const newAttachment: PendingAttachment = {
        file,
        id: `${Date.now()}-${file.name}`,
      }
      return {
        attachments: {
          ...state.attachments,
          [draftId]: [...attachments, newAttachment],
        },
      }
    }),

  removeAttachment: (draftId: string, attachmentId: string) =>
    set((state) => {
      const attachments = state.attachments[draftId] || []
      return {
        attachments: {
          ...state.attachments,
          [draftId]: attachments.filter((a) => a.id !== attachmentId),
        },
      }
    }),

  setTemplateAttachments: (draftId: string, attachments: TemplateAttachment[]) =>
    set((state) => ({
      templateAttachments: {
        ...state.templateAttachments,
        [draftId]: attachments,
      },
    })),

  removeTemplateAttachment: (draftId: string, signedId: string) =>
    set((state) => {
      const attachments = state.templateAttachments[draftId] || []
      return {
        templateAttachments: {
          ...state.templateAttachments,
          [draftId]: attachments.filter((a) => a.signed_id !== signedId),
        },
      }
    }),

  markSignatureAppended: (draftId: string) =>
    set((state) => {
      const newSignatureAppendedDrafts = new Set(state.signatureAppendedDrafts)
      newSignatureAppendedDrafts.add(draftId)
      return { signatureAppendedDrafts: newSignatureAppendedDrafts }
    }),

  hasSignatureAppended: (draftId: string) => get().signatureAppendedDrafts.has(draftId),
}))
